import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxDataGridComponent } from 'devextreme-angular';
import { Subject, takeUntil } from 'rxjs';
import { HoursOption } from '../../shared/models/hours-option';
import { Worker } from '../../shared/models/worker';
import { Registration, SelectedWorkerRegistration } from '../../shared/models/registration';
import { ToolbarService } from '../../shared/services/toolbar.service';
import { WorkerAndToolService } from '../../shared/services/worker-and-tool.service';
import { alert } from 'devextreme/ui/dialog';
import { Constructionsite } from '../../shared/models/constructionsite';
import { RegistrationWorkflowService, WorkflowContext } from '../../shared/services/registration-workflow.service';

@Component({
    selector: 'app-registration-drivers-select',
    templateUrl: './registration-drivers-select.component.html',
    styleUrls: ['./registration-drivers-select.component.scss'],
    standalone: false
})
export class RegistrationDriversSelectComponent {

  @ViewChild(DxDataGridComponent) dataGrid?: DxDataGridComponent;

  registration!: Registration | undefined;
  option: HoursOption | any;
  currentWorker: Worker | undefined;
  constructionSite: Constructionsite | undefined;

  selectedRegistrations: SelectedWorkerRegistration[];

  private unsubscribe: Subject<void> = new Subject<void>();

  hasControls = false;

  constructor(
    private service: WorkerAndToolService,
    private toolbar: ToolbarService,
    private route: ActivatedRoute,
    private router: Router,
    private workflowService: RegistrationWorkflowService) {

    this.selectedRegistrations = [];
    this.registration = service.getNewRegistration();
  }

  ngOnInit() {
    this.service.getHoursOptionObservable().pipe(takeUntil(this.unsubscribe)).subscribe(val => this.option = val);
    this.service.getCurrentWorker().then(w => this.currentWorker = w);

    this.toolbar.backClickedEvent.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.Back());
    this.toolbar.forwardClickedEvent.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.Forward());

    this.toolbar.BackVisibleChanged(true);
    this.toolbar.SaveVisibleChanged(false);
    this.toolbar.ForwardVisibleChanged(true);
    this.toolbar.CancelVisibleChanged(false);
    this.toolbar.PlusVisibleChanged(false);
    this.toolbar.EditVisibleChanged(false);
    this.toolbar.DeleteVisibleChanged(false);
    this.toolbar.CopyVisibleChanged(false);
    this.toolbar.MenuVisibleChanged(false);

    let regId = this.route.snapshot.paramMap.get('id');

    if (regId == null) {
      regId = "-1";
    }

    this.service.getTempRegistration(Number(regId)).then((reg) => {
      this.registration = reg;

      if (this.registration && this.registration.cantiere) {
        this.service.getConstructionSiteByKey(this.registration.cantiere).then((cdc) => this.constructionSite = cdc);
      }

      if (this.registration) {
        this.service.getHasControls(this.registration).then(c => { this.hasControls = c; })

        for (let i = 0; i < this.registration.workerRegistrations.length; i++) {
          const selected = this.registration.workerKilometers.filter(f => this.registration && f.workerNumber === this.registration.workerRegistrations[i].workerNumber).length > 0;
          this.selectedRegistrations.push({
              selected: selected,
              workerNumber: this.registration.workerRegistrations[i].workerNumber,
              workerDescription: this.registration.workerRegistrations[i].workerDescription,
              cost: this.registration.workerRegistrations[i].cost ?? ""
            });
        }
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  async Forward() {
    // commit di eventuali edit pendenti (checkbox "selected" appena toggleata)
    await this.dataGrid?.instance.saveEditData();

    if (this.registration) {
      const currentSelectedRegistrations = this.selectedRegistrations.filter(s => s.selected);
      const ctx: WorkflowContext = { options: this.option, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };

      if (currentSelectedRegistrations.length == 0) {

        this.registration.workerKilometers = [];

        // No drivers selected: skip registration-drivers-kilometers, go to next step after it
        const nextAfterKm = this.workflowService.getNextStep('registration-drivers-kilometers', ctx);

        if (nextAfterKm === null) {
          this.registration.id ??= 0;
          if (this.registration.id > 0) {
            this.service.editRegistration(this.registration, this.registration.id)
              .then(() => this.router.navigate(['/registrations']))
              .catch(err => { if (!this.service.showSaveError(err)) throw err; });
          } else {
            this.service.addRegistration(this.registration)
              .then(() => this.router.navigate(['/registrations']))
              .catch(err => { if (!this.service.showSaveError(err)) throw err; });
          }
        } else {
          this.service.editTempRegistration(this.registration, this.registration.id ?? 0)
            .then((regId) => this.router.navigate(['/' + nextAfterKm, { id: regId }]));
        }
      }
      else {
        // elimino le righe delle registrazioni non presenti tra quelle selezionate
        let kilometers = 0;
        if (this.constructionSite && this.constructionSite.kilometres) kilometers = this.constructionSite.kilometres;
        if (currentSelectedRegistrations.length == 1) kilometers = 2 * kilometers;

        this.registration.workerKilometers = this.registration?.workerKilometers.filter(r => currentSelectedRegistrations.some(s => s.workerNumber == r.workerNumber)) ?? [];

        // Aggiungo le righe delle registrazioni su quelle selezionate,
        // se esiste già non faccio nulla altrimenti aggiungo le ore
        const selectedMissingRegistrations = currentSelectedRegistrations.filter(s => !this.registration?.workerKilometers.some(f => f.workerNumber == s.workerNumber));

        // Salvo la registrazione
        for (let i = 0; i < selectedMissingRegistrations.length; i++) {
          this.registration.workerKilometers.push(
            {
              workerNumber: selectedMissingRegistrations[i].workerNumber,
              workerDescription: selectedMissingRegistrations[i].workerDescription,
              km: kilometers
            }
          );
        }

        // Passo alla pagina successiva
        this.service.editTempRegistration(this.registration, this.registration.id ?? 0).then((regId) => this.router.navigate(['/registration-drivers-kilometers', { id: regId }]))

      }
    }
    else {
      alert("Nessuna registrazione individuata", "Errore");
    }
  }

  Back() {
    if (this.registration) {
      const ctx: WorkflowContext = { options: this.option, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };
      const prev = this.workflowService.getPreviousStep('registration-drivers-select', ctx);
      if (prev) {
        this.service.editTempRegistration(this.registration, this.registration.id ?? 0)
          .then((regId) => this.router.navigate(['/' + prev, { id: regId }]));
      } else {
        this.router.navigate(['/registration']);
      }
    } else {
      alert("Nessuna registrazione individuata", "Errore");
    }
  }
}
